<template>
  <v-dialog v-model="show" max-width="900px" persistent>
    <v-card>
      <v-card-title class="headline">
        <v-icon class="mr-2">mdi-cog-outline</v-icon>
        Manage Model Versions - {{ modelName }}
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="versionHeaders"
          :items="modelVersions"
          :loading="versionsLoading"
          class="elevation-0"
          disable-pagination
          hide-default-footer>
          
          <template v-slot:[`item.thumbnail`]="{ item }">
            <div class="model-thumbnail-cell">
              <v-img
                v-if="getVersionThumbnailUrl(item)"
                :src="getVersionThumbnailUrl(item)"
                width="60"
                height="45"
                class="rounded elevation-1"
                :alt="item.name">
                <template v-slot:placeholder>
                  <div class="thumbnail-placeholder">
                    <v-icon size="16" color="grey">mdi-image</v-icon>
                  </div>
                </template>
              </v-img>
              <div v-else class="thumbnail-placeholder">
                <v-icon size="16" color="primary">mdi-cube-outline</v-icon>
              </div>
            </div>
          </template>
          
          <template v-slot:[`item.version`]="{ item }">
            <v-chip small color="primary" outlined>
              v{{ item.version }}
            </v-chip>
          </template>
          
          <template v-slot:[`item.isSvf2`]="{ item }">
            <v-chip small label v-if="item.isSvf2" color="warning">
              SVF2
            </v-chip>
            <span v-else>-</span>
          </template>
          
          <template v-slot:[`item.createDate`]="{ item }">
            {{ item.createDate ? new Date(item.createDate).toLocaleDateString() : '-' }}
          </template>
          
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              icon
              small
              color="error"
              @click="deleteVersion(item)"
              v-has="permissions.Admin3DModels.Delete">
              <v-icon small>mdi-delete-outline</v-icon>
            </v-btn>
          </template>
          
          <template v-slot:no-data>
            <div class="text-center py-4">
              <v-icon size="48" color="grey lighten-1">mdi-database-outline</v-icon>
              <p class="grey--text mt-2">No versions found</p>
            </div>
          </template>
          
          <template v-slot:loading>
            <div class="text-center py-4">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <p class="mt-2">Loading versions...</p>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="close">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import permissions from '@Share/config/permissions';
import { log, warn, error } from '@/utils/debugLogger';

export default {
  name: 'ManageVersionsDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    modelId: {
      type: [String, Number],
      default: null
    },
    modelName: {
      type: String,
      default: ''
    },
    facilityId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      permissions,
      modelVersions: [],
      versionsLoading: false,
      versionHeaders: [
        { text: '', value: 'thumbnail', sortable: false, width: '100px' },
        { text: 'Version', value: 'version', sortable: true },
        { text: 'Type', value: 'isSvf2', sortable: false },
        { text: 'Created Date', value: 'createDate', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' }
      ]
    };
  },
  computed: {
    ...mapGetters('ADMIN_MODELS', {
      getModelThumbnailFromCache: 'GET_MODEL_THUMBNAIL',
      masters: 'GET_ENTRIES'
    }),
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    forgeService() {
      return this.$store.state.forgeService;
    }
  },
  watch: {
    async value(newVal) {
      if (newVal && this.modelId) {
        await this.loadModelVersions();
      }
    },
    async modelId(newVal) {
      if (newVal && this.show) {
        await this.loadModelVersions();
      }
    }
  },
  methods: {
    ...mapActions('ADMIN_MODELS', ['GET_MODEL_THUMBNAIL', 'GET_MODEL_VERSIONS', 'GET_MASTERS']),
    
    async loadModelVersions() {
      if (!this.modelId || !this.facilityId) return;
      
      this.versionsLoading = true;
      try {
        this.modelVersions = await this.GET_MODEL_VERSIONS({
          facilityId: this.facilityId,
          modelId: this.modelId
        });
        // Load thumbnails for all versions
        this.loadVersionThumbnails();
      } catch (error) {
        error('Error loading model versions:', error);
        this.modelVersions = [];
        this.$dialog.notify.error('Failed to load model versions.', {
          position: 'top-right',
          timeout: 3000,
        });
      } finally {
        this.versionsLoading = false;
      }
    },

    deleteVersion(version) {
      this.$dialog.error({
        text: `Are you sure you want to delete version ${version.version}?`,
        title: `Delete Version`,
        actions: {
          false: 'Cancel',
          true: {
            text: 'Delete',
            color: 'error',
            handle: () => {
              return this.forgeService.deleteVersion(this.facilityId, version.id, async () => {
                try {
                  this.versionsLoading = true;
                  
                  // First reload the master models list to get the updated state
                  await this.GET_MASTERS();
                  
                  // Find the updated model from the masters list to get the new latest version ID
                  const updatedModel = this.masters.find(m => m.name === this.modelName);
                  
                  if (updatedModel) {
                    // Emit event to update the modelId in parent component
                    this.$emit('model-updated', updatedModel);
                    
                    // Load versions for the new latest model ID
                    this.modelVersions = await this.GET_MODEL_VERSIONS({
                      facilityId: this.facilityId,
                      modelId: updatedModel.id
                    });
                    this.loadVersionThumbnails();
                  } else {
                    // If no model found, it might have been completely deleted
                    this.modelVersions = [];
                    this.close();
                  }
                  
                  this.$dialog.notify.success('Version deleted successfully.', {
                    position: 'top-right',
                    timeout: 3000,
                  });
                } catch (error) {
                  error('Error reloading versions after deletion:', error);
                  this.$dialog.notify.error('Error reloading data after deletion.', {
                    position: 'top-right',
                    timeout: 3000,
                  });
                } finally {
                  this.versionsLoading = false;
                }
              });
            },
          },
        },
      });
    },

    async loadVersionThumbnails() {
      if (!this.modelVersions || this.modelVersions.length === 0) return;

      // Load thumbnails for all versions asynchronously
      for (const version of this.modelVersions) {
        if (version.urn && !this.getModelThumbnailFromCache(version.urn)) {
          try {
            await this.GET_MODEL_THUMBNAIL({
              urn: version.urn,
              viewId: version.viewId || null,
              apsRegion: version.apsRegion || null
            });
          } catch (error) {
            error(`Error loading thumbnail for version ${version.version}:`, error);
          }
        }
      }
    },

    getVersionThumbnailUrl(version) {
      if (!version || !version.urn) return null;
      return this.getModelThumbnailFromCache(version.urn, version.viewId || null);
    },

    close() {
      this.modelVersions = [];
      this.versionsLoading = false;
      this.show = false;
    }
  }
};
</script>

<style scoped>
.model-thumbnail-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumbnail-placeholder {
  width: 60px;
  height: 45px;
  background: var(--v-bg1-base);
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
